<template>
  <li class="item-row">
    <div v-if="!shared || canReorderItems" class="drag-handle no-print">
      <v-icon size="small" color="grey-lighten-1">mdi-drag-horizontal</v-icon>
    </div>
    
    <div class="checkbox-container">
      <input 
        type="checkbox" 
        :checked="item.is_checked" 
        class="item-checkbox"
        @change="$emit('updateCheck', item, $event)"
      />
    </div>
    
    <div class="item-content">
      <span class="item-name" :class="{ 'strikethrough': item.is_checked, 'optimistic-item': item._isOptimistic }">
        {{ item.name }}
        <span v-if="calculatedQuantity > 1" class="quantity">({{ calculatedQuantity }}x)</span>
      </span>
      <div v-if="item.description" class="item-description">{{ item.description }}</div>
    </div>
    
    <!-- Optimistic loading/error states -->
    <div v-if="item._isOptimistic" class="optimistic-actions no-print">
      <v-progress-circular
        v-if="item._isLoading"
        size="16"
        width="2"
        color="primary"
        indeterminate
      />
      <div v-else-if="item._hasError" class="error-actions">
        <v-btn
          icon size="x-small"
          variant="text"
          color="orange"
          @click="$emit('retry', item)"
          title="Retry"
        >
          <v-icon size="x-small">mdi-refresh</v-icon>
        </v-btn>
        <v-btn
          icon size="x-small"
          variant="text"
          color="red"
          @click="$emit('remove', item)"
          title="Remove"
        >
          <v-icon size="x-small">mdi-close</v-icon>
        </v-btn>
      </div>
    </div>
    
    <div v-else-if="!shared || canEditItems || canDeleteItems" class="item-actions no-print">
      <v-btn 
        v-if="!shared || canEditItems"
        icon size="x-small" 
        variant="text" 
        @click="$emit('edit', item)" 
        class="action-btn-small"
      >
        <v-icon size="x-small">mdi-pencil</v-icon>
      </v-btn>
      <v-btn 
        v-if="!shared || canDeleteItems"
        icon size="x-small" 
        variant="text" 
        @click="$emit('delete', item)" 
        class="action-btn-small delete-btn"
      >
        <v-icon size="x-small">mdi-delete</v-icon>
      </v-btn>
    </div>
  </li>
</template>

<script>
export default {
  name: 'ItemRow',
  props: {
    item: {
      type: Object,
      required: true
    },
    calculatedQuantity: {
      type: Number,
      default: 1
    },
    shared: {
      type: Boolean,
      default: false
    },
    canEditItems: {
      type: Boolean,
      default: false
    },
    canDeleteItems: {
      type: Boolean,
      default: false
    },
    canReorderItems: {
      type: Boolean,
      default: false
    }
  },
  emits: ['updateCheck', 'edit', 'delete', 'retry', 'remove']
}
</script>

<style scoped>
.item-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.4rem;
  padding: 0.1rem 0;
  break-inside: avoid;
  page-break-inside: avoid;
}

.drag-handle {
  flex-shrink: 0;
  margin-right: 8px;
  padding: 4px;
  cursor: grab;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.drag-handle:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.drag-handle:active {
  cursor: grabbing;
}

.checkbox-container {
  flex-shrink: 0;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
}

.item-checkbox {
  width: 1rem;
  height: 1rem;
  margin: 0;
  cursor: default;
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-weight: 500;
  font-size: 0.95rem;
  color: #333;
  display: block;
  line-height: 1.4;
}

.strikethrough {
  text-decoration: line-through;
  color: #888;
}

.quantity {
  font-weight: 400;
  color: #666;
  margin-left: 0.25rem;
}

.item-description {
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.15rem;
  line-height: 1.2;
}

.item-actions {
  opacity: 0;
  transition: opacity 0.2s ease;
  margin-left: 8px;
}

.item-row:hover .item-actions {
  opacity: 1;
}

.optimistic-actions {
  display: flex;
  align-items: center;
  margin-left: 8px;
  gap: 4px;
}

.optimistic-item {
  opacity: 0.7;
  font-style: italic;
}

.error-actions {
  display: flex;
  gap: 2px;
}

.action-btn-small {
  background-color: rgba(0, 0, 0, 0.1) !important;
  backdrop-filter: blur(4px);
  min-width: 20px !important;
  width: 20px !important;
  height: 20px !important;
}

.action-btn-small:hover {
  background-color: rgba(0, 0, 0, 0.2) !important;
}

.delete-btn:hover {
  background-color: rgba(244, 67, 54, 0.2) !important;
}

.delete-btn:hover .v-icon {
  color: #f44336 !important;
}

/* Default compact sizing */
.item-row {
  margin-bottom: 0.25rem;
  padding: 0.05rem 0;
}

.item-name {
  font-size: 0.85rem;
  line-height: 1.3;
}

.item-description {
  font-size: 0.75rem;
  margin-top: 0.1rem;
  line-height: 1.1;
}

.checkbox-container {
  margin-right: 0.4rem;
  margin-top: 0.1rem;
}

.item-checkbox {
  width: 0.9rem;
  height: 0.9rem;
}

/* Compact sizing for 4-column layout */
@media screen {
  .columns-4 .item-row {
    margin-bottom: 0.15rem;
    padding: 0.025rem 0;
  }

  .columns-4 .item-name {
    font-size: 0.8rem;
    line-height: 1.2;
  }

  .columns-4 .item-description {
    font-size: 0.7rem;
    margin-top: 0.05rem;
    line-height: 1.0;
  }

  .columns-4 .checkbox-container {
    margin-right: 0.3rem;
    margin-top: 0.05rem;
  }

  .columns-4 .item-checkbox {
    width: 0.8rem;
    height: 0.8rem;
  }
}

/* Print styles */
@media print {
  .item-name {
    font-size: 9pt;
  }
  
  .item-description {
    font-size: 8pt;
  }
  
  .item-row {
    margin-bottom: 0.2rem;
  }
  
  /* 4-column specific sizing */
  .columns-4 .item-row {
    margin-bottom: 0.1rem !important;
    padding: 0.02rem 0 !important;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  
  .columns-4 .item-name {
    font-size: 8pt !important;
    line-height: 1.1 !important;
  }
  
  .columns-4 .item-description {
    font-size: 7pt !important;
    margin-top: 0.05rem !important;
    line-height: 1.0 !important;
  }
  
  .columns-4 .checkbox-container {
    margin-right: 0.25rem !important;
    margin-top: 0.02rem !important;
  }
  
  .columns-4 .item-checkbox {
    width: 0.7rem !important;
    height: 0.7rem !important;
  }
}
</style>